/**
 * Maximum character lengths for input fields, mirroring the backend's
 * `HasMaxLength` declarations in `MineTrace.Data/Configuration/*.cs`.
 *
 * Keep this file aligned with the entity Configuration files. When a DB column
 * length changes, update the matching constant here so the UI counter and the
 * native maxLength enforcement stay in sync with what the API actually accepts.
 */

export const PersonFieldLengths = {
  identityDoc: 20,
  names: 200,
  paternalSurname: 100,
  maternalSurname: 100,
  email: 200,
  phoneMobile: 20,
  address: 500,
  ubigeoCode: 10,
} as const

export const CompanyFieldLengths = {
  ruc: 11,
  legalName: 500,
  commercialName: 500,
  taxAddress: 500,
  ubigeoCode: 10,
  mainPhone: 20,
  mainEmail: 200,
  website: 200,
  taxpayerStatus: 50,
  taxpayerCondition: 50,
  taxpayerType: 100,
} as const

export const DriverFieldLengths = {
  licenseNumber: 20,
  licenseCategory: 10,
} as const

export const TruckFieldLengths = {
  placa: 10,
  marca: 100,
  modelo: 100,
  numeroConstanciaInscripcion: 50,
} as const

export const UserFieldLengths = {
  username: 100,
  lastLoginIp: 50,
  lastLoginDevice: 200,
} as const

export const BankAccountFieldLengths = {
  accountNumber: 30,
} as const

export const ConsentTemplateFieldLengths = {
  title: 300,
} as const

export const TenantFieldLengths = {
  code: 50,
  name: 200,
  ruc: 11,
  legalName: 500,
  commercialName: 500,
  taxAddress: 500,
  ubigeoCode: 10,
  prefix: 3,
} as const

export const RoleFieldLengths = {
  code: 50,
  name: 200,
  description: 500,
} as const

export const PermissionFieldLengths = {
  code: 80,
  displayName: 150,
  description: 500,
  module: 50,
  action: 30,
} as const

export const MenuItemFieldLengths = {
  code: 80,
  label: 150,
  route: 250,
  icon: 60,
  requiredPermissionCode: 80,
} as const

export const MeasurementUnitFieldLengths = {
  name: 100,
  abbreviation: 10,
} as const

export const MineralTypeFieldLengths = {
  name: 100,
  code: 20,
  description: 500,
} as const

export const MiningRightFieldLengths = {
  uniqueCode: 50,
  name: 500,
  ubigeoCode: 6,
  reinfoCode: 50,
  concessionCode: 50,
  concessionName: 500,
  reinfoStatus: 50,
} as const

export const RestrictedZoneFieldLengths = {
  sourceLayerCode: 120,
  externalId: 120,
  name: 500,
  category: 200,
  norma: 500,
  entidad: 200,
  sourceUrl: 1000,
} as const

export const FieldVisitScheduleFieldLengths = {
  code: 20,
  ubigeoCode: 6,
} as const

export const ShipmentRecordFieldLengths = {
  batchCode: 50,
  supplierRuc: 11,
  supplierName: 500,
  collectionCenterName: 500,
  mineralTypeName: 200,
  mineralDescription: 500,
  mineralWeight: 50,
  measurementUnitName: 100,
  dispatchGuideNumber: 50,
  dispatchReasonName: 200,
  dispatcherDocNumber: 20,
  dispatcherName: 300,
  dispatchLocation: 500,
  transportGuideNumber: 50,
  vehicleTypeName: 100,
  vehiclePlate: 20,
  driverDocNumber: 20,
  driverName: 300,
  driverLicense: 50,
  deviceId: 100,
  miningRightName: 500,
  miningRightCode: 50,
} as const

export const GeologistFieldLengths = {
  colegiatureCode: 50,
} as const

export const PowerValidityFieldLengths = {
  code: 100,
} as const

export const FormalizedMinerRecordFieldLengths = {
  documentNumber: 11,
  minerName: 300,
  uniqueCode: 50,
  miningRightName: 300,
  department: 100,
  province: 100,
  district: 100,
  resolutionNumber: 300,
  region: 100,
} as const

export const TenantPlantConfigFieldLengths = {
  samplePurposesCsv: 500,
} as const

export const ValidationConfigFieldLengths = {
  codigo: 50,
  nombre: 200,
  descripcion: 500,
  grupoNombre: 100,
  grupoIcono: 10,
} as const

/** Common UI-only limits (not backed by a specific DB column). */
export const UiFieldLengths = {
  /** Generic password input — bcrypt accepts up to 72 bytes, we cap at 128 chars. */
  password: 128,
  /** Free-form search box — large enough for any realistic query. */
  search: 200,
  /** Generic short note / comment. */
  shortNote: 500,
  /** Long-form description / observation. */
  longText: 2000,
} as const
